import { useMemo, useState } from 'react';
import { Clipboard } from 'lucide-react';
import { SegmentTable } from '../components/SegmentTable';
import { VisitTable } from '../components/VisitTable';
import { formatKm } from '../domain/distance';
import type { AnalysisResult } from '../domain/models';
import { renderDailyLog } from '../domain/narrative/TemplateNarrativeProvider';
import { formatDateTime, toLocalDateKey } from '../domain/time';

export function DayDetailPage({ result }: { result: AnalysisResult }) {
  const [selectedDate, setSelectedDate] = useState(result.dailySummaries[0]?.date ?? '');
  const summary = result.dailySummaries.find((day) => day.date === selectedDate) ?? result.dailySummaries[0];
  const markdown = useMemo(() => (summary ? renderDailyLog(summary) : ''), [summary]);
  const visits = result.visits.filter((visit) => summary && toLocalDateKey(visit.startTime) === summary.date);
  const segments = result.segments.filter((segment) => summary && toLocalDateKey(segment.startTime) === summary.date);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Day Detail</h1>
          <p className="page-subtitle">1日単位の滞在候補、移動区間、ルールベース日次ログです。</p>
        </div>
        <div className="field">
          <label htmlFor="day-select">日付</label>
          <select id="day-select" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)}>
            {result.dailySummaries.map((day) => (
              <option key={day.date} value={day.date}>
                {day.date}
              </option>
            ))}
          </select>
        </div>
      </header>

      {summary ? (
        <>
          <section className="grid four">
            <div className="surface stat-card">
              <div className="stat-label">移動距離</div>
              <div className="stat-value">{formatKm(summary.distanceMeters)} km</div>
            </div>
            <div className="surface stat-card">
              <div className="stat-label">外出時間</div>
              <div className="stat-value">{Math.round(summary.activeMinutes)} 分</div>
            </div>
            <div className="surface stat-card">
              <div className="stat-label">滞在時間</div>
              <div className="stat-value">{Math.round(summary.stayMinutes)} 分</div>
            </div>
            <div className="surface stat-card">
              <div className="stat-label">訪問候補</div>
              <div className="stat-value">{summary.visitCount}</div>
            </div>
          </section>

          <section className="split">
            <div className="section">
              <h2 className="section-title">Timeline</h2>
              <div className="surface" style={{ padding: '0 16px' }}>
                <div className="timeline-list">
                  {[...visits, ...segments]
                    .sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime))
                    .map((item) => (
                      <div className="timeline-item" key={item.id}>
                        <div className="timeline-time">
                          {formatDateTime(item.startTime)} - {formatDateTime(item.endTime)}
                        </div>
                        <div>
                          {'center' in item
                            ? `滞在候補: ${item.placeName ?? `${item.center.lat.toFixed(4)}, ${item.center.lng.toFixed(4)}`} / 約 ${Math.round(item.durationMinutes)} 分`
                            : `移動区間: ${formatKm(item.distanceMeters)} km / ${item.inferredMode ?? 'unknown'}`}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="section">
              <div className="section-header">
                <h2 className="section-title">Markdown</h2>
                <button className="button" type="button" onClick={() => navigator.clipboard.writeText(markdown)}>
                  <Clipboard size={16} aria-hidden="true" />
                  コピー
                </button>
              </div>
              <textarea className="textarea" readOnly value={markdown} />
            </div>
          </section>

          <section className="grid two">
            <div className="section">
              <h2 className="section-title">滞在候補</h2>
              <VisitTable visits={visits} />
            </div>
            <div className="section">
              <h2 className="section-title">移動区間</h2>
              <SegmentTable segments={segments} />
            </div>
          </section>
        </>
      ) : (
        <div className="surface map-empty">日次集計はありません</div>
      )}
    </div>
  );
}
